import * as React from 'react';
import API_BASE_URL from '../api';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// DEMO учётка
const DEMO_EMAIL = "demo@radiomonitor.net";

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: 0,
  gap: 0,
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '420px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  borderRadius: 8,
  overflow: 'hidden'
}));

const ForgotPasswordContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100vh',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  background: 'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
}));

export default function ForgotPassword() {
  const { t } = useTranslation('common');
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    try {
      const emailTrim = String(email || '').trim().toLowerCase();

      // ⛔ DEMO: cброс пароля для демо-аккаунта вимкнено — одразу показуємо модалку
      if (emailTrim === DEMO_EMAIL) {
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('demo:block', {
            detail: { action: 'reset_password', source: 'forgot_form' }
          }));
        }
        return;
      }

      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const res = await fetch(`${API}/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        setMessage(t('forgot.sent_ok'));
      } else if (res.status === 403) {
        let data = {};
        try { data = await res.json(); } catch {}
        const detail = data?.detail;
        const code = data?.code || detail?.code || detail?.error;
        if (code === 'DEMO_ADD_BLOCKED') {
          if (typeof window !== 'undefined' && window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('demo:block', {
              detail: { action: 'reset_password', source: 'forgot_form' }
            }));
          }
          return;
        }
        setMessage(t('forgot.user_not_found'));
      } else if (res.status === 404) {
        setMessage(t('forgot.user_not_found'));
      } else {
        setMessage(t('forgot.generic_error'));
      }
    } catch {
      setMessage(t('forgot.generic_error'));
    }
  };

  return (
    <>
      <CssBaseline enableColorScheme />
      <ForgotPasswordContainer direction="column" justifyContent="center" alignItems="center">
        <Card variant="outlined">
          {/* Зеленая шапка */}
          <div style={{
            background: "rgb(0,110,69)",
            color: "#fff",
            height: 74,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 32,
            letterSpacing: 1
          }}>
            {t('forgot.title')}
          </div>

          {message && message === t('forgot.sent_ok')
            ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  gap: 4,
                  padding: "52px 32px 28px 32px",
                  alignItems: "center"
                }}
              >
                <Typography sx={{
                  color: "#006b45",
                  mt: 2,
                  textAlign: 'center',
                  fontSize: 19,
                  mb: 3
                }}>
                  {message}
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    background: "rgb(0,110,69)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 20,
                    border: "none",
                    borderRadius: 1.5,
                    padding: "10px 36px",
                    cursor: "pointer",
                    transition: "background .18s",
                    boxShadow: "0 2px 8px #0001"
                  }}
                  onClick={() => window.history.back()}
                >
                  OK
                </Button>
              </Box>
            )
            : (
              <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  gap: 4,
                  padding: "52px 32px 28px 32px"
                }}
              >

                <TextField
                  id="email"
                  label={t('forgot.email_label')}
                  type="email"
                  value={email}
                  placeholder={t('forgot.email_label')}
                  autoComplete="email"
                  onChange={e => setEmail(e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderRadius: 1,
                    background: "#fff",
                    fontSize: 20,
                    height: 74,
                    "& .MuiInputBase-root": { height: 74, fontSize: 20, alignItems: "center" }
                  }}
                  InputProps={{
                    style: { fontSize: 20, height: 54 }
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    background: "rgb(0,110,69)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 26,
                    border: "none",
                    borderRadius: 1.5,
                    height: 54,
                    cursor: "pointer",
                    transition: "background .18s",
                    boxShadow: "0 2px 8px #0001"
                  }}
                >
                  {t('forgot.submit')}
                </Button>

                {message && message !== t('forgot.sent_ok') && (
                  <Typography sx={{
                    color: "#e00",
                    mt: 2,
                    textAlign: 'center',
                    fontSize: 17
                  }}>
                    {message}
                  </Typography>
                )}
              </Box>
            )
          }
        </Card>
      </ForgotPasswordContainer>
    </>
  );
}
